import { Layer } from "./Layer.js";
import { ContentRenderer } from "./ContentRenderer.js";
import { CYODesign } from "./CYODesign.js";
import * as imageCache from "./imageCache.js";
import {
    getOpaqueBounds,
    loadWalletGripMask,
    createInvertedMask,
    createScaledMask
} from "./CompositorMasks.js";
import { createPrintReadyCanvas as createPrintReadyCanvasImpl } from "./CompositorPrint.js";
import { SurfaceModeRenderer } from "./SurfaceModeRenderer.js";

/**
 * Compositor handles rendering and compositing of multiple image layers
 * 
 * The Compositor class provides functionality to manage multiple layers of images,
 * apply transformations to those layers, and compose them together to create a final
 * rendered image. It supports:
 * 
 * - Layer management (add, remove, get layers)
 * - Layer transformations (scale, rotation, translation)
 * - Layer masking
 * - DOM display setup with configurable scaling
 * 
 * Example usage:
 * 
 * 1. Creating a Compositor from a device and gallery options
 * ```javascript
 * // Set up options
 * const cyoDesign = CYODesign.current.clone();
 * cyoDesign.stickerImages = [...stickerArray];
 * cyoDesign.userImage = myUserImage;
 * cyoDesign.layerData = {
 *   stickers: { scale: 1.2, rotate: 45, translateX: 10, translateY: 20 },
 *   userImage: { scale: 1, rotate: 0, translateX: 0, translateY: 0 }
 * };
 * 
 * // Create compositor - will set up display and layers
 * const container = document.getElementById("display-container");
 * const compositor = await Compositor.createInContainer(container, device, cyoDesign, layerTransformData, device.imageID);
 * 
 * // Get layer objects for manipulation
 * const layerObjects = compositor.getLayerObjects();
 * 
 * // Update a layer's transform and redraw
 * layerObjects.stickers.transform.scale = 1.5;
 * compositor.updateCompositeView();
 * ```
 * 
 * 2. Manual layer creation and compositing
 * ```javascript
 * // Create a compositor
 * const compositor = new Compositor(800, 600);
 * 
 * // Add display to DOM
 * const container = document.getElementById("display-container");
 * compositor.setupDisplay(container, 0.5);
 * 
 * // Create and add layers
 * const bgLayer = compositor.createLayer(backgroundImage, "background");
 * const productLayer = compositor.createLayer(productImage, "product");
 * const stickerLayer = compositor.createLayer(stickerImage, "stickers");
 * 
 * // Transform a layer
 * stickerLayer.transform.scale = 1.2;
 * stickerLayer.transform.rotation = 45;
 * 
 * // Update the view
 * compositor.updateCompositeView();
 * ```
 */
export class Compositor {
    /**
     * Creates a new Compositor instance
     * 
     * @param {number} width - The width of the compositing canvas
     * @param {number} height - The height of the compositing canvas
     */
    constructor(width, height) {
        this.width = width;
        this.height = height;
        this.layers = [];
        this._layerMap = new Map();
        this._finalCanvas = document.createElement("canvas");
        this._finalCanvas.width = width;
        this._finalCanvas.height = height;
        this._displayCanvas = null;
        this._displayContainer = null;
        this.coloredOverlayID = null;
        this._coloredOverlayImage = null;
        this._gripCanvas = null;
    }

    /**
     * Updates the dimensions of the compositor and all its canvases
     * 
     * @param {number} width - New width
     * @param {number} height - New height
     */
    setDimensions(width, height) {
        this.width = width;
        this.height = height;
        this._finalCanvas.width = width;
        this._finalCanvas.height = height;
        
        // Resize all layer canvases
        this.layers.forEach(item => {
            if (item.layer.canvas) {
                item.layer.canvas.width = width;
                item.layer.canvas.height = height;
            }
        });
        
        // Resize display canvas if it exists
        if (this._displayCanvas) {
            this._displayCanvas.width = width;
            this._displayCanvas.height = height;
            // CSS handles the scaling via object-fit: contain
        }
    }

    /**
     * Adds a layer to the compositor
     * 
     * @param {Layer} layer - The layer to add
     * @param {number} zIndex - Z-index for layer ordering (default: layers.length)
     * @returns {Layer} The added layer
     */
    addLayer(layer, zIndex = this.layers.length) {
        if (!(layer instanceof Layer)) {
            throw new Error("Layer must be an instance of Layer");
        }
        
        // Store the layer with its z-index
        this.layers.push({
            layer,
            zIndex
        });
        
        // Sort layers by z-index
        this.layers.sort((a, b) => a.zIndex - b.zIndex);
        
        // Add to map for fast lookup
        this._layerMap.set(layer.id, layer);
        
        return layer;
    }

    /**
     * Gets a layer by ID
     * 
     * @param {string} id - The ID of the layer to retrieve
     * @returns {Layer|undefined} The layer if found, undefined otherwise
     */
    getLayer(id) {
        return this._layerMap.get(id);
    }

    /**
     * Removes a layer by ID
     * 
     * @param {string} id - The ID of the layer to remove
     * @returns {Layer|null} The removed layer or null if not found
     */
    removeLayer(id) {
        const index = this.layers.findIndex(item => item.layer.id === id);
        if (index !== -1) {
            const removed = this.layers.splice(index, 1)[0];
            this._layerMap.delete(id);
            return removed.layer;
        }
        return null;
    }

    /**
     * Creates a new layer and adds it to the compositor
     * 
     * @param {HTMLCanvasElement|HTMLImageElement} image - The source image for the layer
     * @param {string} id - The ID for the new layer
     * @returns {Layer} The created layer
     */
    createLayer(image, id) {
        const layer = new Layer(image, id);
        this.addLayer(layer);
        return layer;
    }

    /**
     * Composites all layers to create a final canvas
     * 
     * Draws all visible layers onto the final canvas in order of z-index
     * using each layer's blend mode.
     * 
     * @returns {HTMLCanvasElement} The final composited canvas
     */
    composite() {
        const ctx = this._finalCanvas.getContext("2d");
        ctx.clearRect(0, 0, this.width, this.height);
        
        // Process only visible layers
        for (const { layer } of this.layers) {
            if (!layer.visible) continue;
            
            // Render the layer with its transformation
            const layerCanvas = layer.render(this.width, this.height);
            if (!layerCanvas) continue;
            
            // Set the blend mode
            ctx.globalCompositeOperation = layer.blendMode;
            
            // Draw the layer
            ctx.drawImage(layerCanvas, 0, 0);
            
            // Reset blend mode
            ctx.globalCompositeOperation = "source-over";
        }
        
        return this._finalCanvas;
    }

    /**
     * Applies a mask layer to another layer
     * 
     * @param {string} layerId - ID of the layer to be masked
     * @param {string} maskLayerId - ID of the layer to use as a mask
     */
    applyMaskToLayer(layerId, maskLayerId) {
        const layer = this.getLayer(layerId);
        const maskLayer = this.getLayer(maskLayerId);
        
        if (layer && maskLayer) {
            // Make sure both layers are rendered
            layer.render(this.width, this.height);
            maskLayer.render(this.width, this.height);
            
            // Apply the mask
            layer.applyMask(maskLayer);
        }
    }

    /**
     * Gets a tree of all layer canvases plus the final composite
     * 
     * @returns {Object} Object with layer ID keys pointing to canvases
     */
    getLayerTree() {
        const result = {};
        
        // Add all layers to the result
        for (const { layer } of this.layers) {
            if (layer.visible && layer.canvas) {
                result[layer.id] = layer.canvas;
            }
        }
        
        // Add the final composite
        result.final = this._finalCanvas;
        
        return result;
    }
    
    /**
     * Creates a Compositor instance from a device and gallery options using a ContentRenderer
     * 
     * This factory method handles the entire process of:
     * 1. Running the render pass to generate layer images
     * 2. Setting up the compositor with the correct dimensions
     * 3. Creating a display container if needed
     * 4. Creating and configuring all required layers
     * 5. Performing the initial composite render
     * 
     * @param {HTMLElement} container - DOM element to append the display to (optional)
     * @param {Object} device - The device to render
     * @param {Object} cyoDesign - Gallery options containing render settings
     * @param {Object} layerTransformData - Transform data for layers
     * @param {string} productImageID - The product image ID to use
     * @param {string} coloredOverlayImageID - The image ID of an optional colored overlay. Currently for wallet only.
     * @param {Object} options - Optional configuration.
     * @param {boolean} options.isAltCompositor - True if this is an alt compositor (grip surface of wallet in split mode).
     * @returns {Compositor} A configured Compositor instance ready for use. Caller must call updateCompositeView() to render.
     */
    static async createInContainer(container, device, cyoDesign, layerTransformData, productImageID, coloredOverlayImageID = null, options = {}) {
        if (!device) {
            console.warn("[Compositor] createInContainer called with null device, skipping");
            return null;
        }

        // Load device images first
        const { productImage, maskImage, overlayImage, coloredOverlayImage } = await Compositor.loadDeviceImages(device, productImageID, coloredOverlayImageID);
        
        const contentRenderer = new ContentRenderer(device, cyoDesign);
        contentRenderer.stickerImages = cyoDesign.stickerImages || [];
        contentRenderer.backgroundImage = cyoDesign.backgroundImage || null;
        contentRenderer.transparentImage = cyoDesign.transparentImage || null;
        contentRenderer.userImage = cyoDesign.userImage || null;

        // In split mode, wallet surface stickers should not reserve space for user image
        // (user image only appears on grip via altCompositor)
        // Only apply to wallet device, not to grip device (altCompositor)
        if (cyoDesign.walletPrintSurface === CYODesign.WalletPrintSurface.split && device.productCategory === "wallet") {
            contentRenderer.ignorePhotoPlacement = true;
        }

        // In split mode, grip surface should not reserve space for stickers
        // (stickers only appear on wallet via main compositor)
        // Only apply when rendering the grip surface of a wallet (altCompositor), not standalone grip products
        if (options.isAltCompositor && cyoDesign.walletPrintSurface === CYODesign.WalletPrintSurface.split && device.productCategory === "grip") {
            contentRenderer.ignoreStickerPlacement = true;
        }

        // Pass loaded device images to ContentRenderer (except overlay which is handled by Compositor)
        contentRenderer.clientProductImage = productImage;
        contentRenderer.clientProductMaskImage = maskImage;

        const layerTree = await contentRenderer.draw();
        
        // Extract dimensions from the product layer
        const productLayer = layerTree.product;
        const width = productLayer.width;
        const height = productLayer.height;
        
        // Create compositor with extracted dimensions
        const compositor = new Compositor(width, height);

        // Store device and gallery options for layer ordering logic
        compositor._device = device;
        compositor._cyoDesign = cyoDesign;
                
        // Set up display container and canvas if a container was provided
        if (container) {
            compositor.setupDisplay(container, device);
        }
        
        // Store layer canvases and create layers for compositor
        const layerObjects = {};
        const requiredLayers = ["product", "bgImage", "stickers", "photoStickers", "mask", "userImage", "debugOverlay"];
        
        // Create overlay layer directly from loaded image if it exists
        if (overlayImage) {
            const overlayCanvas = document.createElement("canvas");
            overlayCanvas.width = width;
            overlayCanvas.height = height;
            const overlayCtx = overlayCanvas.getContext("2d");
            overlayCtx.drawImage(overlayImage, 0, 0, width, height);
            
            const overlayLayer = new Layer(overlayCanvas, "overlay");
            overlayLayer.transform = null; // Static layer
            compositor.addLayer(overlayLayer);
            layerObjects["overlay"] = overlayLayer;
        }
        
        // Create individual layers for the compositor
        requiredLayers.forEach(layerKey => {
            const sourceLayer = layerTree[layerKey];
            if (sourceLayer) {
                // Create a layer for this canvas
                const layer = new Layer(sourceLayer, layerKey);
                
                // Set visibility (bgImage and debugOverlay should be visible by default)
                if (layerKey === "bgImage" || layerKey === "debugOverlay") {
                    layer.visible = true;
                }
                
                // Set transform to null for static layers so that slower Layer._renderWithTransform isn't used.
                if (layerKey === "mask" || layerKey === "product" || layerKey === "bgImage" || layerKey === "overlay" || layerKey === "debugOverlay") {
                    layer.transform = null;
                }
                
                // Don't set transform to null for expanded canvases - they need transforms for gestures
                // The Layer class will handle using the fast render path for expanded canvases
                // Set initial transform if specified in layerData (for transformable layers only)
                else if (layerTransformData && layerTransformData[layerKey]) {
                    const singleLayerTransformData = layerTransformData[layerKey];
                    layer.transform.scale = singleLayerTransformData.scale || 1;
                    layer.transform.rotation = singleLayerTransformData.rotate || 0;
                    layer.transform.translateX = singleLayerTransformData.translateX || 0;
                    layer.transform.translateY = singleLayerTransformData.translateY || 0;
                }
                
                // Add the layer to the compositor
                compositor.addLayer(layer);
                
                // Store for reference
                layerObjects[layerKey] = layer;
            }
        });
        
        // Store layer objects for external access
        compositor._layerObjects = layerObjects;

        // Store content renderer for partial re-rendering
        compositor._contentRenderer = contentRenderer;
        
        // Store colored overlay image if loaded
        if (coloredOverlayImage) {
            compositor.coloredOverlayID = coloredOverlayImageID;
            compositor._coloredOverlayImage = coloredOverlayImage;
        }

        // Load wallet-grip mask for wallet devices (used for surface-specific rendering)
        compositor._walletGripMaskCanvas = null;
        compositor._invertedWalletGripMaskCanvas = null;
        compositor._gripBounds = null;
        if (device.productCategory === "wallet") {
            try {
                const walletGripMaskImage = await loadWalletGripMask();
                compositor._walletGripMaskCanvas = createScaledMask(walletGripMaskImage, width, height);
                compositor._invertedWalletGripMaskCanvas = createInvertedMask(walletGripMaskImage, width, height);
                // Calculate grip bounds from the inverted mask (grip area is opaque)
                compositor._gripBounds = getOpaqueBounds(compositor._invertedWalletGripMaskCanvas);
            } catch (error) {
                console.warn("Failed to load wallet-grip mask:", error);
            }
        }

        return compositor;
    }
    
    /**
     * Creates a composite canvas for CMS use cases without DOM setup
     * 
     * This is a simplified factory method specifically designed for CMS operations
     * like generating preview composites. It handles the entire process of:
     * 1. Creating a temporary container to ensure display canvas creation
     * 2. Setting up the compositor with all layers and colored overlays
     * 3. Returning the final composite canvas directly
     * 
     * This method eliminates the need to create temporary DOM elements manually
     * and ensures that colored overlays are properly applied.
     * 
     * @param {Object} device - The device to render
     * @param {Object} cyoDesign - Gallery options containing render settings
     * @param {Object} layerTransformData - Transform data for layers (optional)
     * @param {string} productImageID - The product image ID to use
     * @param {string} coloredOverlayImageID - The image ID of an optional colored overlay (optional)
     * @returns {HTMLCanvasElement} The final composite canvas ready for use
     */
    static async createCompForCMS(device, cyoDesign, layerTransformData = null, productImageID = null, coloredOverlayImageID = null) {
        // Use device.imageID as fallback if no specific productImageID provided
        const finalProductImageID = productImageID || device.imageID;
        
        // Create a temporary container to ensure display canvas creation
        const tempContainer = document.createElement("div");
        
        // Create the compositor with all proper setup
        const compositor = await Compositor.createInContainer(
            tempContainer,
            device,
            cyoDesign,
            layerTransformData,
            finalProductImageID,
            coloredOverlayImageID
        );
        compositor.updateCompositeView(null);

        // Return the display canvas which includes all layers and overlays
        return compositor._displayCanvas;
    }
    
    /**
     * Creates a print-ready canvas containing only content (background + user image + stickers)
     * without product image, mask, or overlay visible in the final result
     *
     * @param {Object} device - The device to render
     * @param {Object} cyoDesign - Gallery options containing render settings
     * @param {Object} layerTransformData - Transform data for layers (optional)
     * @param {string} productImageID - The product image ID to use for masking
     * @param {number} randomSeed - Random seed for deterministic rendering (optional)
     * @param {Object} altDevice - Alternative device for grip surface in independent both mode (optional)
     * @param {Object} altLayerTransformData - Transform data for alt device layers (optional)
     * @returns {HTMLCanvasElement} A print-ready canvas with masked content only
     */
    static async createPrintReadyCanvas(device, cyoDesign, layerTransformData = null, productImageID = null, randomSeed = null, altDevice = null, altLayerTransformData = null) {
        return createPrintReadyCanvasImpl(
            device,
            cyoDesign,
            layerTransformData,
            productImageID,
            randomSeed,
            altDevice,
            altLayerTransformData,
            Compositor.loadDeviceImages
        );
    }
        
    /**
     * Sets up a DOM display container and canvas for rendering
     * 
     * Creates and configures a container and canvas for displaying the composite result
     * in the DOM. The canvas maintains its internal dimensions for proper rendering but
     * uses CSS to scale to fit its container.
     * 
     * @param {HTMLElement} container - DOM element to append the display to
     * @param {Object} device - The device object containing productCategory
     * @returns {HTMLElement} The created display container
     */
    setupDisplay(container, device = null) {
        // Create a parent container for the display
        if (!this._displayContainer) {
            this._displayContainer = document.createElement("div");
            this._displayContainer.className = "composite-container";
            this._displayContainer.style.position = "relative";
            this._displayContainer.style.width = "100%";
            this._displayContainer.style.height = "100%";
            this._displayContainer.style.backgroundColor = "transparent";
            this._displayContainer.style.margin = "0 12px";
            this._displayContainer.style.boxSizing = "border-box";

            
            // Set max-width based on device productCategory
            if (device && device.productCategory) {
                const maxWidths = {
                    "grip": "400px",
                    "case": "400px", 
                    "wallet": "400px"
                };
                const maxWidth = maxWidths[device.productCategory];
                if (maxWidth) {
                    this._displayContainer.style.maxWidth = maxWidth;
                }
            }
            
            // Create the display canvas
            this._displayCanvas = document.createElement("canvas");
            this._displayCanvas.width = this.width;
            this._displayCanvas.height = this.height;
            // Use CSS to make canvas responsive
            this._displayCanvas.style.width = "100%";
            this._displayCanvas.style.height = "100%";
            this._displayCanvas.style.objectFit = "contain";
            this._displayCanvas.style.display = "block";
            this._displayCanvas.style.backgroundColor = "transparent";
            this._displayCanvas.className = "composite-canvas";
            
            // Add the canvas to the container
            this._displayContainer.appendChild(this._displayCanvas);

            // Create grip canvas for wallet products (always visible and used for rotation animation)
            if (device && device.productCategory === "wallet") {
                this._gripCanvas = document.createElement("canvas");
                this._gripCanvas.className = "grip-canvas";
                this._gripCanvas.style.position = "absolute";
                this._gripCanvas.style.pointerEvents = "none";
                this._gripCanvas.style.willChange = "transform";
                this._gripCanvas.style.transformOrigin = "center center";
                // Grip canvas is always visible for wallet products
                this._displayContainer.appendChild(this._gripCanvas);
            }

            // Add the display container to the provided container
            container.appendChild(this._displayContainer);
        }

        return this._displayContainer;
    }

    /**
     * Calculate the display (CSS) position and size for grip bounds
     * Accounts for object-fit: contain scaling
     * @returns {Object|null} {left, top, width, height, scaleX, scaleY} in CSS pixels, or null
     */
    _calculateGripDisplayBounds() {
        if (!this._gripBounds || !this._displayCanvas) return null;

        const canvasRect = this._displayCanvas.getBoundingClientRect();
        const canvasAspectRatio = this.width / this.height;
        const displayAspectRatio = canvasRect.width / canvasRect.height;

        let actualDisplayWidth, actualDisplayHeight, offsetX, offsetY;

        if (canvasAspectRatio > displayAspectRatio) {
            // Canvas is wider - fit by width, center vertically
            actualDisplayWidth = canvasRect.width;
            actualDisplayHeight = canvasRect.width / canvasAspectRatio;
            offsetX = 0;
            offsetY = (canvasRect.height - actualDisplayHeight) / 2;
        } else {
            // Canvas is taller - fit by height, center horizontally
            actualDisplayWidth = canvasRect.height * canvasAspectRatio;
            actualDisplayHeight = canvasRect.height;
            offsetX = (canvasRect.width - actualDisplayWidth) / 2;
            offsetY = 0;
        }

        // Scale factors from native canvas to display size
        const scaleX = actualDisplayWidth / this.width;
        const scaleY = actualDisplayHeight / this.height;

        return {
            left: offsetX + (this._gripBounds.x * scaleX),
            top: offsetY + (this._gripBounds.y * scaleY),
            width: this._gripBounds.width * scaleX,
            height: this._gripBounds.height * scaleY,
            scaleX: scaleX,
            scaleY: scaleY,
            offsetX: offsetX,
            offsetY: offsetY
        };
    }

    /**
     * Update grip canvas position/size to match current display
     */
    _updateGripCanvasPosition() {
        if (!this._gripCanvas || !this._gripBounds) return;

        // Use sqrt(2) padding for 45-degree rotation safety
        const padding = 1.42;

        // Always set native canvas resolution from gripBounds (doesn't need layout)
        this._gripCanvas.width = Math.ceil(this._gripBounds.width * padding);
        this._gripCanvas.height = Math.ceil(this._gripBounds.height * padding);

        // CSS positioning requires display bounds (needs layout to be complete)
        const displayBounds = this._calculateGripDisplayBounds();
        if (!displayBounds || displayBounds.width === 0) {
            // Can't position via CSS yet, but canvas is sized for rendering
            return;
        }

        // CSS position (centered on grip center)
        const paddedWidth = displayBounds.width * padding;
        const paddedHeight = displayBounds.height * padding;
        const centerX = displayBounds.left + displayBounds.width / 2;
        const centerY = displayBounds.top + displayBounds.height / 2;

        this._gripCanvas.style.width = `${paddedWidth}px`;
        this._gripCanvas.style.height = `${paddedHeight}px`;
        this._gripCanvas.style.left = `${centerX - paddedWidth / 2}px`;
        this._gripCanvas.style.top = `${centerY - paddedHeight / 2}px`;

        // Store for rendering
        this._gripCanvasDisplayBounds = displayBounds;
    }

    /**
     * Render grip-only content to the separate grip canvas
     * Called by updateCompositeView after main canvas is rendered
     * @param {HTMLCanvasElement} sourceCanvas - Canvas with full render (before grip masking)
     */
    _renderGripCanvasFromSource(sourceCanvas) {
        if (!this._gripCanvas || !this._gripBounds || !this._invertedWalletGripMaskCanvas) return;

        // Ensure grip canvas position is up to date
        this._updateGripCanvasPosition();

        const gripCtx = this._gripCanvas.getContext("2d", { alpha: true });
        const canvasWidth = this._gripCanvas.width;
        const canvasHeight = this._gripCanvas.height;

        gripCtx.clearRect(0, 0, canvasWidth, canvasHeight);

        // Calculate offset to center grip content in padded canvas
        const gripWidth = this._gripBounds.width;
        const gripHeight = this._gripBounds.height;
        const offsetX = (canvasWidth - gripWidth) / 2;
        const offsetY = (canvasHeight - gripHeight) / 2;

        // Create a temp canvas at grip size for compositing
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = gripWidth;
        tempCanvas.height = gripHeight;
        const tempCtx = tempCanvas.getContext("2d", { alpha: true });

        // Copy the grip portion from the source canvas
        tempCtx.drawImage(
            sourceCanvas,
            this._gripBounds.x, this._gripBounds.y, gripWidth, gripHeight,
            0, 0, gripWidth, gripHeight
        );

        // Apply grip mask to isolate grip shape (remove any content outside grip)
        tempCtx.globalCompositeOperation = "destination-in";
        tempCtx.drawImage(
            this._invertedWalletGripMaskCanvas,
            this._gripBounds.x, this._gripBounds.y, gripWidth, gripHeight,
            0, 0, gripWidth, gripHeight
        );
        tempCtx.globalCompositeOperation = "source-over";

        // Draw the masked content to the grip canvas with padding offset
        gripCtx.drawImage(tempCanvas, offsetX, offsetY);
    }

    /**
     * Updates and renders the composite view with specified layers or all layers
     *
     * Renders the specified layers with their current transformations and composites them
     * onto the display canvas in the correct order, applying masking as needed.
     * This should be called any time a layer's visibility or transform changes.
     *
     * @param {Array<string>|null} layerIds - Array of layer IDs to update, or null to update all layers
     * @param {Compositor|null} altCompositor - Optional alt compositor for split mode (provides user image with grip-style rendering)
     * @returns {HTMLCanvasElement|null} The display canvas or null if not available
     */
    updateCompositeView(layerIds = null, altCompositor = null) {
        // Early return if no display canvas
        if (!this._displayCanvas) return;
        
        // Render specified layers or all layers if layerIds is null
        if (layerIds === null) {
            // Render all layers
            for (const layerId in this._layerObjects) {
                const layer = this._layerObjects[layerId];
                if (layer) {
                    layer.render(this.width, this.height);
                }
            }
        } else {
            // Render only specified layers
            for (const layerId of layerIds) {
                const layer = this._layerObjects[layerId];
                if (layer) {
                    layer.render(this.width, this.height);
                }
            }
        }
        
        // Draw to the display canvas for proper layering
        const ctx = this._displayCanvas.getContext("2d", { alpha: true });
        ctx.clearRect(0, 0, this.width, this.height);
        
        // Special case: If debugIsolateMask is true, show only the mask as a regular image
        if (this._cyoDesign.debugOptions.isolateMask && this._layerObjects["mask"]) {
            ctx.drawImage(this._layerObjects["mask"].canvas, 0, 0);
            return this._displayCanvas;
        }
        
        // Draw the product layer first (unless debugHideProduct is true)
        if (this._layerObjects["product"] && !this._cyoDesign.debugOptions.hideProduct) {
            ctx.drawImage(this._layerObjects["product"].canvas, 0, 0);
        }
        
        // Create a temporary canvas for content that will be masked
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = this.width;
        tempCanvas.height = this.height;
        const tempCtx = tempCanvas.getContext("2d", { alpha: true });
        
        // Get surface mode early - needed for z-ordering decisions
        const walletPrintSurface = this._cyoDesign.walletPrintSurface || "wallet";

        // In "both" mode, background, user image, and stickers are drawn later (after colored overlay)
        // so they appear on both surfaces with stickers on top of user image
        const isBothMode = walletPrintSurface === "both";
        // Independent "both" mode: each surface renders its own border/pattern/corner treatment
        const isBothModeIndependent = isBothMode && this._cyoDesign?.requiresIndependentSurfaceRendering?.(this._device);

        // Draw background image first (it goes under everything else)
        // Exception: In "both" mode, background is drawn later
        const bgLayer = this._layerObjects["bgImage"];
        if (bgLayer && bgLayer.visible && !isBothMode) {
            tempCtx.drawImage(bgLayer.canvas, 0, 0);
        }

        // Draw user content (userImage, stickers, and photoStickers) to the temp canvas in the correct order
        const userImageLayer = this._layerObjects["userImage"];
        const stickersLayer = this._layerObjects["stickers"];
        const photoStickersLayer = this._layerObjects["photoStickers"];

        if (this._cyoDesign && userImageLayer && stickersLayer) {
            // Get the user image from the layer's source if available
            const userImgVariant = userImageLayer.sourceImage;
            // Get sticker images from gallery options if available
            const stickerImages = this._cyoDesign.stickerImages || [];
            
            // Check what should draw on top
            let whatOnTop = this._cyoDesign.whatToDrawOnTop(userImgVariant, stickerImages);

            // For wallet products in "wallet" or regular "both" mode, stickers go on top of user image
            // EXCEPT when subjectStyle is "removeBackground" with center/bottom placement (user image on top)
            const isWalletProduct = this._device?.productCategory === "wallet";
            const isRemoveBgCenterOrBottom = this._cyoDesign.subjectStyle === CYODesign.Style.removeBackground &&
                (this._cyoDesign.subjectPlacement === CYODesign.Placement.center || this._cyoDesign.subjectPlacement === CYODesign.Placement.bottom);
            const forceStickersOnTop = isWalletProduct && (walletPrintSurface === "wallet" || (isBothMode && !isBothModeIndependent)) && !isRemoveBgCenterOrBottom;
            if (forceStickersOnTop) {
                whatOnTop = CYODesign.StickerOrPhoto.sticker;
            }

            if (whatOnTop === CYODesign.StickerOrPhoto.sticker) {
                // Stickers on top - draw user image first, then stickers
                // In "both" mode, user image is drawn later (after colored overlay)
                if (userImageLayer.visible && !isBothMode) {
                    // Check if this is an expanded canvas that needs special positioning
                    if (userImageLayer.canvas._isExpandedCanvas) {
                        // Apply the viewport offset to center the expanded canvas content
                        const baseOffsetX = -(userImageLayer.canvas._viewportOffsetX || 0);
                        const baseOffsetY = -(userImageLayer.canvas._viewportOffsetY || 0);

                        if (userImageLayer.transform && (userImageLayer.transform.scale !== 1 || userImageLayer.transform.rotation !== 0 || userImageLayer.transform.translateX !== 0 || userImageLayer.transform.translateY !== 0)) {
                            // Apply full transform (scale, rotation, translation) around the visible image center
                            tempCtx.save();

                            // Use stored rotation pivot (center of visible image) or fall back to canvas center
                            const pivotX = userImageLayer.canvas._rotationPivotX ?? userImageLayer.canvas._originalCanvasWidth / 2;
                            const pivotY = userImageLayer.canvas._rotationPivotY ?? userImageLayer.canvas._originalCanvasHeight / 2;

                            // Apply transforms in the correct order: translate to pivot + user translation, rotate, scale, translate back
                            tempCtx.translate(pivotX + userImageLayer.transform.translateX, pivotY + userImageLayer.transform.translateY);
                            tempCtx.rotate(userImageLayer.transform.rotation * Math.PI / 180);
                            tempCtx.scale(userImageLayer.transform.scale, userImageLayer.transform.scale);
                            tempCtx.translate(-pivotX, -pivotY);

                            tempCtx.drawImage(userImageLayer.canvas, baseOffsetX, baseOffsetY);
                            tempCtx.restore();
                        } else {
                            // No transform needed, just apply viewport offset for centering
                            tempCtx.drawImage(userImageLayer.canvas, baseOffsetX, baseOffsetY);
                        }
                    } else {
                        // Normal canvas - no special positioning needed
                        tempCtx.drawImage(userImageLayer.canvas, 0, 0);
                    }
                }
                if (photoStickersLayer && photoStickersLayer.visible) {
                    tempCtx.drawImage(photoStickersLayer.canvas, 0, 0);
                }
                // Skip stickers here if regular "both" mode - they'll be drawn on top of user image later
                // (Independent both mode handles stickers separately in renderIndependentBothMode)
                if (stickersLayer.visible && !(isBothMode && !isBothModeIndependent)) {
                    tempCtx.drawImage(stickersLayer.canvas, 0, 0);
                }
            } else {
                // User image on top - draw stickers first, then user image
                // Skip stickers here if regular "both" mode - they'll be drawn on top of user image later
                if (stickersLayer.visible && !(isBothMode && !isBothModeIndependent)) {
                    tempCtx.drawImage(stickersLayer.canvas, 0, 0);
                }
                if (photoStickersLayer && photoStickersLayer.visible) {
                    tempCtx.drawImage(photoStickersLayer.canvas, 0, 0);
                }
                // In "both" mode, user image is drawn later (after colored overlay)
                if (userImageLayer.visible && !isBothMode) {
                    // Check if this is an expanded canvas that needs special positioning
                    if (userImageLayer.canvas._isExpandedCanvas) {
                        const baseOffsetX = -(userImageLayer.canvas._viewportOffsetX || 0);
                        const baseOffsetY = -(userImageLayer.canvas._viewportOffsetY || 0);

                        if (userImageLayer.transform && (userImageLayer.transform.scale !== 1 || userImageLayer.transform.rotation !== 0 || userImageLayer.transform.translateX !== 0 || userImageLayer.transform.translateY !== 0)) {
                            tempCtx.save();
                            const pivotX = userImageLayer.canvas._rotationPivotX ?? userImageLayer.canvas._originalCanvasWidth / 2;
                            const pivotY = userImageLayer.canvas._rotationPivotY ?? userImageLayer.canvas._originalCanvasHeight / 2;
                            tempCtx.translate(pivotX + userImageLayer.transform.translateX, pivotY + userImageLayer.transform.translateY);
                            tempCtx.rotate(userImageLayer.transform.rotation * Math.PI / 180);
                            tempCtx.scale(userImageLayer.transform.scale, userImageLayer.transform.scale);
                            tempCtx.translate(-pivotX, -pivotY);
                            tempCtx.drawImage(userImageLayer.canvas, baseOffsetX, baseOffsetY);
                            tempCtx.restore();
                        } else {
                            tempCtx.drawImage(userImageLayer.canvas, baseOffsetX, baseOffsetY);
                        }
                    } else {
                        tempCtx.drawImage(userImageLayer.canvas, 0, 0);
                    }
                }
            }
        } else {
            // Fallback to original order if we don't have the necessary data
            // Skip stickers here if regular "both" mode - they'll be drawn on top of user image later
            if (stickersLayer && stickersLayer.visible && !(isBothMode && !isBothModeIndependent)) {
                tempCtx.drawImage(stickersLayer.canvas, 0, 0);
            }
            if (photoStickersLayer && photoStickersLayer.visible) {
                tempCtx.drawImage(photoStickersLayer.canvas, 0, 0);
            }
            // In "both" mode, user image is drawn later (after colored overlay)
            if (userImageLayer && userImageLayer.visible && !isBothMode) {
                // Check if this is an expanded canvas that needs special positioning
                if (userImageLayer.canvas._isExpandedCanvas) {
                    const baseOffsetX = -(userImageLayer.canvas._viewportOffsetX || 0);
                    const baseOffsetY = -(userImageLayer.canvas._viewportOffsetY || 0);

                    if (userImageLayer.transform && (userImageLayer.transform.scale !== 1 || userImageLayer.transform.rotation !== 0 || userImageLayer.transform.translateX !== 0 || userImageLayer.transform.translateY !== 0)) {
                        tempCtx.save();
                        const pivotX = userImageLayer.canvas._rotationPivotX ?? userImageLayer.canvas._originalCanvasWidth / 2;
                        const pivotY = userImageLayer.canvas._rotationPivotY ?? userImageLayer.canvas._originalCanvasHeight / 2;
                        tempCtx.translate(pivotX + userImageLayer.transform.translateX, pivotY + userImageLayer.transform.translateY);
                        tempCtx.rotate(userImageLayer.transform.rotation * Math.PI / 180);
                        tempCtx.scale(userImageLayer.transform.scale, userImageLayer.transform.scale);
                        tempCtx.translate(-pivotX, -pivotY);
                        tempCtx.drawImage(userImageLayer.canvas, baseOffsetX, baseOffsetY);
                        tempCtx.restore();
                    } else {
                        tempCtx.drawImage(userImageLayer.canvas, baseOffsetX, baseOffsetY);
                    }
                } else {
                    tempCtx.drawImage(userImageLayer.canvas, 0, 0);
                }
            }
        }

        // Apply surface-specific masking for wallet products
        // Independent "both" mode: each surface renders its own treatment
        if (isBothModeIndependent && this._walletGripMaskCanvas && this._invertedWalletGripMaskCanvas && this._gripBounds && altCompositor) {
            const surfaceRenderer = new SurfaceModeRenderer(this);
            const result = surfaceRenderer.renderIndependentBothMode(tempCtx, altCompositor);
            this._bothModeIndependentGripContent = result.gripContent;
            this._bothModeIndependentCenterContent = result.centerContent;
        } else if ((walletPrintSurface === "grip" || walletPrintSurface === "split") && this._invertedWalletGripMaskCanvas && this._gripBounds) {
            // GRIP or SPLIT mode: User image on grip (with grip-style placements)
            const surfaceRenderer = new SurfaceModeRenderer(this);
            const result = surfaceRenderer.renderGripOrSplitMode(tempCtx, altCompositor, walletPrintSurface);
            this._splitModeGripContent = result.gripContent;
        }
        // For "wallet" and "both" modes: no surface masking - content renders everywhere
        // (content shows through translucent grip product image)

        // Apply the product mask to the temp canvas if available (unless debugHideMask is true)
        if (this._layerObjects["mask"] && !this._cyoDesign.debugOptions.hideMask) {
            tempCtx.globalCompositeOperation = "destination-out";
            tempCtx.drawImage(this._layerObjects["mask"].canvas, 0, 0);
            tempCtx.globalCompositeOperation = "source-over";
        }

        // Check if we'll be doing split rendering with content visible through grip
        // (need to know this before drawing colored overlay to avoid doubling up)
        const gripDisplayBounds = this._gripCanvas ? this._calculateGripDisplayBounds() : null;
        const canPositionGripCanvas = gripDisplayBounds && gripDisplayBounds.width > 0;
        const willSplitRenderWithVisibleContent = this._gripCanvas && canPositionGripCanvas &&
            (walletPrintSurface === "wallet" || walletPrintSurface === "both" || walletPrintSurface === "split");

        // Draw user content and colored overlay with appropriate z-order
        // For "wallet", "both", and "split" modes: colored overlay goes ON TOP of stickers (translucent grip effect)
        // For "grip" mode: colored overlay goes UNDER user content (shows through transparent areas)
        if (walletPrintSurface === "wallet" || walletPrintSurface === "both" || walletPrintSurface === "split") {
            ctx.drawImage(tempCanvas, 0, 0);
            if (this._coloredOverlayImage && !this._cyoDesign.debugOptions.hideColoredOverlay) {
                if (willSplitRenderWithVisibleContent && this._invertedWalletGripMaskCanvas) {
                    // For wallet/both with split rendering: mask colored overlay to exclude grip area
                    // (grip canvas will provide the colored overlay for the grip area)
                    const maskedOverlay = document.createElement("canvas");
                    maskedOverlay.width = this.width;
                    maskedOverlay.height = this.height;
                    const maskedCtx = maskedOverlay.getContext("2d", { alpha: true });
                    maskedCtx.drawImage(this._coloredOverlayImage, 0, 0, this.width, this.height);
                    // Remove grip area from overlay
                    maskedCtx.globalCompositeOperation = "destination-out";
                    maskedCtx.drawImage(this._invertedWalletGripMaskCanvas, 0, 0);
                    maskedCtx.globalCompositeOperation = "source-over";
                    ctx.drawImage(maskedOverlay, 0, 0);
                } else {
                    ctx.drawImage(this._coloredOverlayImage, 0, 0, this.width, this.height);
                }
            }
            // For "split" mode: draw grip content (user image) ON TOP of colored overlay
            // Skip if doing split rendering - grip content will go on grip canvas instead
            if (walletPrintSurface === "split" && this._splitModeGripContent && !willSplitRenderWithVisibleContent) {
                ctx.drawImage(this._splitModeGripContent, 0, 0);
                this._splitModeGripContent = null; // Clear after use
            }
            // For independent "both" mode: draw center user image content ON TOP of colored overlay
            // This goes before grip content (user image should be under grip-specific treatments)
            if (isBothModeIndependent && this._bothModeIndependentCenterContent) {
                ctx.drawImage(this._bothModeIndependentCenterContent, 0, 0);
                this._bothModeIndependentCenterContent = null; // Clear after use
            }
            // For independent "both" mode: draw grip content ON TOP of colored overlay (and center content)
            // Skip if doing split rendering - grip content will go on grip canvas instead
            if (isBothModeIndependent && this._bothModeIndependentGripContent && !willSplitRenderWithVisibleContent) {
                ctx.drawImage(this._bothModeIndependentGripContent, 0, 0);
                this._bothModeIndependentGripContent = null; // Clear after use
            }
            // For regular "both" mode (not independent): draw background, user image, and stickers ON TOP of colored overlay
            // These need to be masked to the product bounds
            // Skip this for independent both mode since we already handled it above
            if (isBothMode && !isBothModeIndependent && (bgLayer?.visible || userImageLayer?.visible || photoStickersLayer?.visible || stickersLayer?.visible)) {
                const bothModeCanvas = document.createElement("canvas");
                bothModeCanvas.width = this.width;
                bothModeCanvas.height = this.height;
                const bothModeCtx = bothModeCanvas.getContext("2d", { alpha: true });

                // Draw background first
                if (bgLayer && bgLayer.visible) {
                    bothModeCtx.drawImage(bgLayer.canvas, 0, 0);
                }
                // Draw photoStickers (user images in border/pattern/corner placement) on top of background
                if (photoStickersLayer && photoStickersLayer.visible) {
                    bothModeCtx.drawImage(photoStickersLayer.canvas, 0, 0);
                }
                // Draw user image with transforms (for center/bottom placement)
                if (userImageLayer && userImageLayer.visible) {
                    if (userImageLayer.canvas._isExpandedCanvas) {
                        const baseOffsetX = -(userImageLayer.canvas._viewportOffsetX || 0);
                        const baseOffsetY = -(userImageLayer.canvas._viewportOffsetY || 0);

                        if (userImageLayer.transform && (userImageLayer.transform.scale !== 1 || userImageLayer.transform.rotation !== 0 || userImageLayer.transform.translateX !== 0 || userImageLayer.transform.translateY !== 0)) {
                            bothModeCtx.save();
                            const pivotX = userImageLayer.canvas._rotationPivotX ?? userImageLayer.canvas._originalCanvasWidth / 2;
                            const pivotY = userImageLayer.canvas._rotationPivotY ?? userImageLayer.canvas._originalCanvasHeight / 2;
                            bothModeCtx.translate(pivotX + userImageLayer.transform.translateX, pivotY + userImageLayer.transform.translateY);
                            bothModeCtx.rotate(userImageLayer.transform.rotation * Math.PI / 180);
                            bothModeCtx.scale(userImageLayer.transform.scale, userImageLayer.transform.scale);
                            bothModeCtx.translate(-pivotX, -pivotY);
                            bothModeCtx.drawImage(userImageLayer.canvas, baseOffsetX, baseOffsetY);
                            bothModeCtx.restore();
                        } else {
                            bothModeCtx.drawImage(userImageLayer.canvas, baseOffsetX, baseOffsetY);
                        }
                    } else {
                        bothModeCtx.drawImage(userImageLayer.canvas, 0, 0);
                    }
                }

                // In regular "both" mode, draw stickers ON TOP of user image
                if (stickersLayer && stickersLayer.visible) {
                    bothModeCtx.drawImage(stickersLayer.canvas, 0, 0);
                }

                // Apply product mask
                if (this._layerObjects["mask"] && !this._cyoDesign.debugOptions.hideMask) {
                    bothModeCtx.globalCompositeOperation = "destination-out";
                    bothModeCtx.drawImage(this._layerObjects["mask"].canvas, 0, 0);
                    bothModeCtx.globalCompositeOperation = "source-over";
                }

                // Store for grip canvas (regular both mode needs bg+user image on grip too)
                if (willSplitRenderWithVisibleContent) {
                    this._bothModeRegularGripContent = bothModeCanvas;
                }

                // Draw masked content to main canvas
                ctx.drawImage(bothModeCanvas, 0, 0);
            }
        } else {
            if (this._coloredOverlayImage && !this._cyoDesign.debugOptions.hideColoredOverlay) {
                ctx.drawImage(this._coloredOverlayImage, 0, 0, this.width, this.height);
            }
            ctx.drawImage(tempCanvas, 0, 0);
        }

        // Show/hide grip canvas based on whether it can be positioned
        if (this._gripCanvas) {
            this._gripCanvas.style.display = canPositionGripCanvas ? "block" : "none";
        }

        if (this._gripCanvas && this._invertedWalletGripMaskCanvas && canPositionGripCanvas) {
            // Create grip source canvas
            const gripSourceCanvas = document.createElement("canvas");
            gripSourceCanvas.width = this.width;
            gripSourceCanvas.height = this.height;
            const gripSourceCtx = gripSourceCanvas.getContext("2d", { alpha: true });

            // Determine what goes on grip canvas and whether to mask main canvas
            // "wallet" mode: content on wallet surface, visible through translucent grip - DON'T mask main canvas
            // "both" mode: content on both surfaces, visible through translucent grip - DON'T mask main canvas
            // "split" mode: stickers on wallet, user image on grip - wallet content visible through grip - DON'T mask main canvas
            // "grip" mode: all content only on grip - DO mask main canvas to avoid double rendering
            const contentVisibleThroughGrip = (walletPrintSurface === "wallet" || walletPrintSurface === "both" || walletPrintSurface === "split");

            if (contentVisibleThroughGrip) {
                // Draw colored overlay for grip rotation (translucent grip effect)
                // Product image stays on main canvas - we don't want to double it up
                // Content stays on main canvas visible through translucent grip
                if (this._coloredOverlayImage && !this._cyoDesign.debugOptions.hideColoredOverlay) {
                    gripSourceCtx.drawImage(this._coloredOverlayImage, 0, 0, this.width, this.height);
                }
                // For "split" mode: draw grip content (user image) ON TOP of colored overlay
                if (this._splitModeGripContent) {
                    gripSourceCtx.drawImage(this._splitModeGripContent, 0, 0);
                    this._splitModeGripContent = null; // Clear after use
                }
                // For "both" mode: draw independent grip content ON TOP of colored overlay
                if (this._bothModeIndependentGripContent) {
                    gripSourceCtx.drawImage(this._bothModeIndependentGripContent, 0, 0);
                    this._bothModeIndependentGripContent = null; // Clear after use
                }
                // For regular "both" mode: draw bg+user image content ON TOP of colored overlay
                if (this._bothModeRegularGripContent) {
                    gripSourceCtx.drawImage(this._bothModeRegularGripContent, 0, 0);
                    this._bothModeRegularGripContent = null; // Clear after use
                }
            } else {
                // For grip mode, capture full render (content is on grip)
                gripSourceCtx.drawImage(this._displayCanvas, 0, 0);

                // Mask out grip area from main canvas (content is only on grip canvas)
                ctx.globalCompositeOperation = "destination-out";
                ctx.drawImage(this._invertedWalletGripMaskCanvas, 0, 0);
                ctx.globalCompositeOperation = "source-over";
            }

            // Render grip canvas from captured state (deferred to after overlay)
            this._pendingGripSource = gripSourceCanvas;
        }

        // Apply overlay if available (goes on top of everything, unless debugHideOverlay is true)
        if (this._layerObjects["overlay"] && this._layerObjects["overlay"].visible && !this._cyoDesign.debugOptions.hideOverlay) {
            ctx.drawImage(this._layerObjects["overlay"].canvas, 0, 0);
        }

        // Apply safe area debug if available (goes on top of overlay)
        if (this._layerObjects["debugOverlay"] && this._layerObjects["debugOverlay"].visible) {
            ctx.drawImage(this._layerObjects["debugOverlay"].canvas, 0, 0);
        }

        // Render grip canvas from the captured state (after overlay is on main canvas)
        if (this._pendingGripSource) {
            this._renderGripCanvasFromSource(this._pendingGripSource);
            this._pendingGripSource = null;
        }

        return this._displayCanvas;
    }
    
    /**
     * Returns the layer objects managed by this Compositor
     *
     * @returns {Object} A map of layer objects by ID
     */
    getLayerObjects() {
        return this._layerObjects;
    }

    /**
     * Updates a layer's canvas content in-place without recreating the layer
     * Used for partial re-rendering (e.g., surface mode changes)
     *
     * @param {string} layerId - The ID of the layer to update
     * @param {HTMLCanvasElement} newCanvas - The new canvas content
     */
    updateLayerCanvas(layerId, newCanvas) {
        const layer = this._layerObjects[layerId];
        if (layer && newCanvas) {
            layer.sourceImage = newCanvas;
            // Don't set layer.canvas - let render() update it from sourceImage
            // This is important for layers that use _renderWithTransform
        }
    }

    /**
     * Returns the display container element
     * 
     * @returns {HTMLElement|null} The display container or null if not created yet
     */
    getDisplayContainer() {
        return this._displayContainer;
    }
    
    /**
     * Loads device images (product, mask, overlay, colored overlay) from the device configuration
     * 
     * @param {Object} device - The device object containing maskID and overlayID
     * @param {string} productImageID - The product image ID to use
     * @param {string|null} coloredOverlayImageID - The colored overlay image ID to use (optional)
     * @param {number} maxWidth - Maximum width for the images (default: 400)
     * @param {number} quality - Image quality (default: 85)
     * @returns {Object} Object containing loaded images: { productImage, maskImage, overlayImage, coloredOverlayImage }
     */
    static async loadDeviceImages(device, productImageID, coloredOverlayImageID = null, maxWidth = 400, quality = 85) {
        const dpr = Math.round(window.devicePixelRatio);

        // Load product and mask images (only if productImageID is provided)
        let productImage = null;
        let maskImage = null;
        if (productImageID) {
            [productImage, maskImage] = await Promise.all([
                imageCache.getLoadAndStoreCF(productImageID, maxWidth, "webp", quality, dpr),
                device.maskID ? imageCache.getLoadAndStoreCF(device.maskID, maxWidth, "webp", quality, dpr) : null
            ]);
        }
        
        // Load overlay image if available (optional, only when loading product images)
        let overlayImage = null;
        if (productImageID && device.overlayID) {
            overlayImage = await imageCache.getLoadAndStoreCF(device.overlayID, maxWidth, "webp", quality, dpr);
        }
        
        // Load colored overlay image if available (optional)
        let coloredOverlayImage = null;
        if (coloredOverlayImageID) {
            coloredOverlayImage = await imageCache.getLoadAndStoreCF(coloredOverlayImageID, maxWidth, "webp", quality, dpr);
        }
        
        return { productImage, maskImage, overlayImage, coloredOverlayImage };
    }

    /**
     * Sets the colored overlay image ID and loads the corresponding image
     * 
     * @param {string|null} imageID - The Cloudflare Images ID for the colored overlay
     */
    async setColoredOverlayID(imageID) {
        this.coloredOverlayID = imageID;
        this._coloredOverlayImage = null;
        
        if (imageID) {
            try {
                const maxPrdImageW = 400;
                const dpr = Math.round(window.devicePixelRatio);
                this._coloredOverlayImage = await imageCache.getLoadAndStoreCF(imageID, maxPrdImageW, "png", 100, dpr);
            } catch (error) {
                console.warn(`Failed to load colored overlay image: ${imageID}`, error);
                this._coloredOverlayImage = null;
            }
        }
    }
}